import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSesion, type Permiso } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import { es, type Clave } from "../idiomas/es";

/**
 * Los primeros pasos, en la pantalla de inicio y solo mientras hagan falta.
 *
 * Un club que acaba de entrar ve ceros por todas partes y ninguna pista de qué
 * hacer primero. Esto se lo dice, en el orden en que tiene sentido hacerlo, y
 * desaparece solo cuando el club ya está funcionando.
 *
 * Cada paso lleva al sitio donde se hace. Un tutorial que te explica algo pero
 * no te lleva allí obliga a buscarlo en el menú, y entonces ya has perdido a la
 * mitad.
 */

function delDiccionario(
  t: (c: Clave) => string,
  paso: string,
  parte: "titulo" | "detalle",
  delServidor: string,
) {
  const clave = `pasos.${paso}.${parte}` as Clave;
  return clave in es ? t(clave) : delServidor;
}

interface Paso {
  clave: string;
  titulo: string;
  detalle: string;
  hecho: boolean;
  ruta: string;
  permiso: string;
}

interface Respuesta {
  pasos: Paso[];
  hechos: number;
  total: number;
  mostrar: boolean;
}

export default function PrimerosPasos() {
  const { t } = useIdioma();
  const { puede } = useSesion();
  const consulta = useQuery({
    queryKey: ["primeros-pasos"],
    queryFn: () => api<Respuesta>("/informes/primeros-pasos"),
  });

  const datos = consulta.data;
  if (!datos?.mostrar) return null;

  /*
   * Solo los pasos que esta persona puede hacer. A quien está en el mostrador
   * no se le enseña "completa los datos del club": no puede entrar ahí, y un
   * paso que no se puede dar solo sirve para agobiar.
   */
  const mios = datos.pasos.filter((p) => puede(p.permiso as Permiso));
  if (mios.length === 0 || mios.every((p) => p.hecho)) return null;

  const hechos = mios.filter((p) => p.hecho).length;

  return (
    <section className="primeros-pasos">
      <div className="fila separada" style={{ marginBottom: 10 }}>
        <div>
          <h2>{t("pp.paraEmpezar")}</h2>
          <p className="suave pequeno" style={{ margin: 0 }}>
            {t("pp.enEsteOrden")}
          </p>
        </div>
        <span className="pasos-cuenta">{t("pp.hechosDe", { hechos, total: mios.length })}</span>
      </div>

      <ol className="lista-pasos">
        {mios.map((paso) => (
          <li key={paso.clave} className={paso.hecho ? "hecho" : undefined}>
            <span className="marca" aria-hidden="true">
              {paso.hecho ? "✓" : "○"}
            </span>
            {/*
              El titulo y el detalle los manda la API en castellano, pero cada
              paso viene con su clave: se traduce por ella y solo se usa el
              texto del servidor si es un paso que esta pantalla no conoce.
            */}
            <div>
              <strong>{delDiccionario(t, paso.clave, "titulo", paso.titulo)}</strong>
              <p>{delDiccionario(t, paso.clave, "detalle", paso.detalle)}</p>
            </div>
            {!paso.hecho && (
              <Link to={paso.ruta} className="boton secundario pequeno">
                {t("pp.ir")}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
